// BoutiqueShowcase, a fashion-house experience for bridal boutiques / designers,
// built for the bride's emotional journey (Discover → Dream → Experience → Connect),
// not a venue review with dresses inserted.
//
// The bride is asking "is this me?", so we open on one gown and one statement, tell
// the designer's story, honour the craft, walk the collection, play the film, then
// reach the centrepiece nobody else can follow, Experience the Collection, before
// real brides and a private appointment.
//
// DATA-DRIVEN + COLLECTION-AGNOSTIC. Every beat reads its content from the showcase's
// `sections` array by type and renders ONLY when that content exists, so a sparse
// boutique still looks composed and next season's collection just swaps the images.
// Nothing brand-specific is hardcoded here, this component is shared by every
// boutique; the words live in each listing's data.
//
// Section types read: hero, designer (falls back to intro), craft, gallery, film
// (falls back to hero.video), experience (the try-on centrepiece, gated on .enabled),
// brides, atelier.
import { useState, useRef, useEffect } from 'react';
import { imgUrl } from '../utils/imgUrl';
import { track } from '../utils/track';
import { supabase } from '../lib/supabaseClient';
import { getOrCreateAnonId } from '../services/auraIdentity';

const GD   = 'var(--font-heading-primary)';
const NU   = 'var(--font-body)';
// Palette is theme-driven: these resolve to CSS variables set on the root wrapper
// from the darkMode prop, so every surface flips with the homepage lights toggle.
const GOLD  = 'var(--bq-gold)';
const CREAM = 'var(--bq-cream)';
const INK   = 'var(--bq-ink)';
const INK2  = 'var(--bq-ink2)';
const DARK  = 'var(--bq-base)';
const PAPER = 'var(--bq-paper)';
const WHITE = 'var(--bq-white)';
const LINE  = 'var(--bq-line)';
const SCRIM = 'var(--bq-scrim)';   // lightbox backdrop
const FROST = 'var(--bq-frost)';   // frosted-glass controls floating over images

// Light = the current Net-a-Porter cream. Dark ("lights off") = a deep warm
// near-black with cream ink and a single gold accent for the over-image kickers.
const PALETTE_LIGHT = {
  '--bq-gold': '#161616', '--bq-cream': '#f6f4ef', '--bq-ink': '#161616',
  '--bq-ink2': '#76736d', '--bq-base': '#f6f4ef', '--bq-paper': '#f6f4ef',
  '--bq-white': '#ffffff', '--bq-line': 'rgba(0,0,0,0.14)',
  '--bq-scrim': 'rgba(246,244,239,0.97)', '--bq-frost': FROST,
};
const PALETTE_DARK = {
  '--bq-gold': '#c9a96e', '--bq-cream': '#15110f', '--bq-ink': '#f2eee7',
  '--bq-ink2': 'rgba(242,238,231,0.60)', '--bq-base': '#0d0b0a', '--bq-paper': '#100d0c',
  '--bq-white': '#1c1815', '--bq-line': 'rgba(255,255,255,0.13)',
  '--bq-scrim': 'rgba(11,9,8,0.97)', '--bq-frost': 'rgba(28,23,19,0.72)',
};
// The gallery lightbox is always dark, ivory gowns disappear against cream.
// These vars override the page palette on the lightbox container so all children
// (counter, captions, action bar) inherit light-on-dark automatically.
const LB_PALETTE = {
  '--bq-gold': '#c9a96e', '--bq-ink': '#ece8e1', '--bq-ink2': 'rgba(236,232,225,0.52)',
  '--bq-line': 'rgba(255,255,255,0.12)', '--bq-scrim': 'rgba(8,6,5,0.97)',
  '--bq-frost': 'rgba(18,14,11,0.82)', '--bq-white': '#1e1915',
};

// Like/Save persist per device (no login needed); engagement analytics flow via track().
function loadSet(key) { try { return new Set(JSON.parse(localStorage.getItem(key) || '[]')); } catch { return new Set(); } }
function saveSet(key, set) { try { localStorage.setItem(key, JSON.stringify([...set])); } catch {} }
// Em-dashes are an AI tell and banned in copy; swap any in displayed captions for
// a small dash (data may still carry them, e.g. media captions).
const noEm = (s) => (typeof s === 'string' ? s.replace(/\s*—\s*/g, ' - ') : s);

function FaqItem({ q, a, INK, INK2, LINE, NU, GD, isMobile }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderTop: `1px solid ${LINE}` }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', background: 'none', border: 'none', padding: isMobile ? '22px 0' : '26px 0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ fontFamily: GD, fontWeight: 400, fontSize: isMobile ? 17 : 20, color: INK, lineHeight: 1.3 }}>{q}</span>
        <span style={{ flexShrink: 0, fontFamily: NU, fontSize: 18, color: INK2, lineHeight: 1, transform: open ? 'rotate(45deg)' : 'none', transition: 'transform .2s ease' }}>+</span>
      </button>
      {open && (
        <p style={{ fontFamily: NU, fontWeight: 300, fontSize: isMobile ? 14 : 15, lineHeight: 1.9, color: INK2, margin: '0 0 24px', maxWidth: 640 }}>{a}</p>
      )}
    </div>
  );
}

export default function BoutiqueShowcase({ showcase, listing, preview, onViewListing, onAppointment, magazineSlug, siteChrome, darkMode }) {
  const palette = darkMode ? PALETTE_DARK : PALETTE_LIGHT;
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const touchX = useRef(0);
  const durationRef = useRef(0);
  const seekingRef = useRef(false);
  const barRef = useRef(null);
  const hideTimerRef = useRef(null);
  const coupleRef = useRef(null); // cached couple_id for house save persistence
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0); // 0..1 film position
  const [filmControls, setFilmControls] = useState(true); // film controls visible (auto-hide while playing)
  const [lightbox, setLightbox] = useState(null);
  const [selectedLook, setSelectedLook] = useState(null); // last gown she engaged with, anchors the form
  const [appt, setAppt] = useState({ name: '', email: '', message: '', weddingDate: '', weddingLocation: '', budget: '', appointmentType: '' });
  const [apptState, setApptState] = useState('idle'); // idle | sending | sent
  const [apptTypes, setApptTypes] = useState([]); // fashion_attributes.appointment_type (data-driven; empty = selector hidden)
  const [savedLooks, setSavedLooks] = useState([]); // this bride's try-on looks for THIS house (data-gated, no empty block)

  // "Your Saved Looks", the visible proof the system remembers her. Reads her
  // own bridal_looks for this house (by device anon id, or couple_id once known).
  useEffect(() => {
    const id = listing?.id;
    if (!id) { setSavedLooks([]); return undefined; }
    let ignore = false;
    (async () => {
      try {
        const anonId = getOrCreateAnonId();
        const { data: auth } = await supabase.auth.getUser();
        let coupleId = null;
        if (auth?.user) {
          const { data: c } = await supabase.from('couples').select('id').eq('user_id', auth.user.id).maybeSingle();
          coupleId = c?.id ?? null;
        }
        if (coupleId) coupleRef.current = coupleId;
        const ors = [`anonymous_id.eq.${anonId}`];
        if (coupleId) ors.push(`couple_id.eq.${coupleId}`);
        const { data } = await supabase
          .from('bridal_looks')
          .select('id, result_image_url, created_at')
          .eq('listing_id', id)
          .not('result_image_url', 'is', null)
          .or(ors.join(','))
          .order('created_at', { ascending: false })
          .limit(3);
        if (!ignore) setSavedLooks(data || []);
      } catch { if (!ignore) setSavedLooks([]); }
    })();
    return () => { ignore = true; };
  }, [listing?.id]);

  // Appointment options come from the listing's fashion_attributes, never
  // hardcoded per house. Empty -> the selector is not shown (no fabricated options).
  useEffect(() => {
    const id = listing?.id;
    if (!id) { setApptTypes([]); return undefined; }
    let ignore = false;
    supabase.from('fashion_attributes').select('appointment_type').eq('listing_id', id).maybeSingle()
      .then(({ data }) => { if (!ignore) setApptTypes(Array.isArray(data?.appointment_type) ? data.appointment_type : []); });
    return () => { ignore = true; };
  }, [listing?.id]);
  const listingId = listing?.id || null;
  const lsKey = (kind) => `lwd_boutique_${kind}_${listingId || 'x'}`;
  const [likes, setLikes] = useState(() => loadSet(lsKey('likes')));
  const [saves, setSaves] = useState(() => loadSet(lsKey('saves')));

  // When a logged-in bride first engages with any look in this house, persist the
  // house to couple_saved_listings. Anonymous: localStorage-only; migrated on login.
  const upsertHouseSave = () => {
    const cid = coupleRef.current;
    const lid = listingId;
    if (!cid || !lid) return;
    supabase.from('couple_saved_listings')
      .upsert({ couple_id: cid, listing_id: lid, context: 'fashion_house' },
               { onConflict: 'couple_id,listing_id,context' })
      .then(() => {});
  };
  const [shareFor, setShareFor] = useState(null); // the look whose share menu is open

  // Read content blocks via the showcase (loaded through a SECURITY-DEFINER RPC, so
  // always present). Each beat degrades gracefully if its block is missing.
  const secs = showcase?.sections || showcase?.published_sections || [];
  const sec = (t) => (secs.find((s) => s.type === t) || {}).content || {};
  const heroC = sec('hero');
  const designerC = sec('designer');
  const introC = sec('intro');
  const craftC = sec('craft');
  const galleryC = sec('gallery');
  const filmC = sec('film');
  const experienceC = sec('experience');
  const bridesC = sec('brides');
  const atelierC = sec('atelier');
  const tryonC   = sec('tryon');
  const stylistC = sec('stylist');
  const faqC     = sec('faq');

  // Collection-agnostic look set: the current collection's images, or the listing's media.
  const galleryImgs = (galleryC.images || []).filter((g) => g && g.url);
  const media = listing?.media_items || [];
  const looks = galleryImgs.length
    ? galleryImgs
    : media.filter((m) => (m.type || 'image') === 'image' && m.url).map((m) => ({ url: m.url, caption: m.caption }));
  const collectionName = galleryC.collection || experienceC.collection || 'The Collection';

  const name = preview?.name || heroC.title || showcase?.title || listing?.name || 'The Atelier';
  const eyebrowKicker = heroC.kicker || 'Couture Bridal Atelier';

  // 1, The Collection: one still gown, one statement (identity, not logistics).
  const openImg = heroC.image || looks[0]?.url;
  const openStatement = (heroC.statement || heroC.tagline || '').trim();

  // 2, The Designer.
  const designerStory = designerC.body || introC.body || (listing?.description || '').split('\n\n')[0] || '';
  const designerName = designerC.name || '';
  const designerHeadline = designerC.headline || 'The hand behind every bride.';
  const designerEyebrow = designerC.eyebrow || 'The Couturier';

  // 3, The Craft (e.g. the Total Look). Renders only with real content.
  const craftBody = craftC.body || '';
  const craftQuote = craftC.quote || '';
  const craftHeadline = craftC.headline || '';
  const showCraft = Boolean(craftBody || craftQuote);

  // 5, Film. A re-hosted mp4 or a YouTube link.
  const filmVideo = filmC.video || heroC.video || null;
  const ytMatch = (filmVideo || '').match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  const ytId = ytMatch ? ytMatch[1] : null;
  const ytOrigin = typeof window !== 'undefined' ? encodeURIComponent(window.location.origin) : '';
  const ytSrc = ytId
    ? `https://www.youtube-nocookie.com/embed/${ytId}?autoplay=1&mute=1&controls=0&rel=0&modestbranding=1&loop=1&playlist=${ytId}&playsinline=1&disablekb=1&iv_load_policy=3&enablejsapi=1&origin=${ytOrigin}`
    : null;

  // 6, Experience the Collection (the centrepiece). Gated until the studio is proven,
  // so we never frame a broken result as "see yourself in the collection".
  const expEnabled = experienceC.enabled === true;
  const expCollection = experienceC.collection || collectionName;
  const studioHref = experienceC.href || (listing?.slug ? `/fashion-studio?boutique=${listing.slug}&collection=${encodeURIComponent(expCollection)}` : null);
  const showExperience = expEnabled && Boolean(studioHref);

  // 7, Real Brides. Hidden until a real, attributable story exists. Never invented.
  const brides = (bridesC.items || []).filter((b) => b && (b.quote || b.body));

  // 8, Visit the Atelier.
  const atelierHeadline = atelierC.headline || 'Book a private fitting.';
  const atelierBody = atelierC.body
    || `${name} receives brides at the atelier and by arrangement worldwide. Request a private appointment and the atelier will be in touch.`;
  const atelierAddress = atelierC.address || '';
  const showAppointment = atelierC.appointment !== false;
  // Budget bands are universal; currency follows the house's country (no assumption
  // of sterling). Italy -> EUR for Ferdinand.
  const CURRENCY_BY_COUNTRY = { italy: '€', france: '€', spain: '€', greece: '€', portugal: '€', germany: '€', austria: '€', ireland: '€', england: '£', scotland: '£', wales: '£', 'united-kingdom': '£', uk: '£', usa: '$', 'united-states': '$' };
  const cur = CURRENCY_BY_COUNTRY[(listing?.country_slug || '').toLowerCase()] || '€';
  const budgetBands = [`${cur}2,500 to ${cur}5,000`, `${cur}5,000 to ${cur}10,000`, `${cur}10,000+`];
  // Smooth-scroll helper for the "Before your fitting" links.
  const scrollTo = (id) => { try { document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch {} };

  const toggleSound = () => {
    if (ytId) {
      const f = iframeRef.current;
      if (f?.contentWindow) f.contentWindow.postMessage(JSON.stringify({ event: 'command', func: muted ? 'unMute' : 'mute', args: [] }), '*');
    }
    setMuted((m) => !m);
  };
  const togglePlay = () => {
    if (ytId) {
      const f = iframeRef.current;
      if (f?.contentWindow) f.contentWindow.postMessage(JSON.stringify({ event: 'command', func: playing ? 'pauseVideo' : 'playVideo', args: [] }), '*');
    } else {
      const v = videoRef.current;
      if (v) { if (playing) v.pause(); else v.play().catch(() => {}); }
    }
    setPlaying((p) => !p);
  };
  const revealFilmControls = () => {
    setFilmControls(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setFilmControls(false), 2600);
  };
  useEffect(() => {
    if (!filmVideo) return undefined;
    const t = setTimeout(() => setFilmControls(false), 2600);
    return () => { clearTimeout(t); if (hideTimerRef.current) clearTimeout(hideTimerRef.current); };
  }, [filmVideo]);
  const seekFrac = (frac) => {
    const f = Math.max(0, Math.min(1, frac));
    if (ytId) {
      const fr = iframeRef.current;
      if (fr?.contentWindow && durationRef.current) fr.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'seekTo', args: [durationRef.current * f, true] }), '*');
    } else {
      const v = videoRef.current; if (v && v.duration) v.currentTime = v.duration * f;
    }
    setProgress(f);
  };
  const scrubFrom = (clientX) => {
    const el = barRef.current; if (!el) return null;
    const r = el.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - r.left) / r.width));
  };
  const onScrubDown = (e) => {
    e.stopPropagation();
    seekingRef.current = true;
    const f0 = scrubFrom(e.clientX); if (f0 != null) seekFrac(f0);
    const move = (ev) => { const f = scrubFrom(ev.clientX); if (f != null) setProgress(f); };
    const up = (ev) => { const f = scrubFrom(ev.clientX); if (f != null) seekFrac(f); seekingRef.current = false;
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  useEffect(() => {
    if (ytId) return;
    const v = videoRef.current;
    if (v) { v.muted = muted; if (!muted && playing) v.play().catch(() => {}); }
  }, [muted, ytId, playing]);
  // YouTube position/duration via the iframe API (infoDelivery messages).
  useEffect(() => {
    if (!ytId) return undefined;
    const onMsg = (e) => {
      if (typeof e.data !== 'string' || !/youtube/.test(e.origin)) return;
      let d; try { d = JSON.parse(e.data); } catch { return; }
      const info = d && d.info;
      if (!info) return;
      if (info.duration) durationRef.current = info.duration;
      if (typeof info.playerState === 'number') setPlaying(info.playerState === 1);
      if (typeof info.currentTime === 'number' && durationRef.current && !seekingRef.current) {
        setProgress(info.currentTime / durationRef.current);
      }
    };
    window.addEventListener('message', onMsg);
    const ask = () => { const f = iframeRef.current; if (f?.contentWindow) f.contentWindow.postMessage(JSON.stringify({ event: 'listening', id: 1 }), '*'); };
    const t = setInterval(ask, 800); ask();
    return () => { window.removeEventListener('message', onMsg); clearInterval(t); };
  }, [ytId]);
  const submitAppt = async () => {
    if (!appt.name.trim() || !appt.email.trim()) return;
    setApptState('sending');
    try { await onAppointment?.(appt); } catch {}
    setApptState('sent');
  };

  // ── Engagement: view tracking, like, save, share. Collection-agnostic: look_key = image url. ──
  const lookPayload = (look) => ({ vendor_id: listingId, category: 'bridal_boutique',
    look_key: look?.url, look_caption: look?.caption || null, collection: collectionName });
  // Gallery slider: lightbox holds an INDEX into `looks` (null = closed).
  const openLook = (look) => {
    const l = typeof look === 'string' ? { url: look } : (look || {});
    if (!l.url || !looks.length) return;
    const idx = looks.findIndex((x) => x.url === l.url);
    const at = idx >= 0 ? idx : 0;
    setLightbox(at);
    setShareFor(null);
    track('boutique_look_view', lookPayload(looks[at] || l));
  };
  const navLook = (dir) => {
    if (lightbox == null || looks.length < 2) return;
    const n = (lightbox + dir + looks.length) % looks.length;
    setLightbox(n);
    setShareFor(null);
    if (looks[n]) track('boutique_look_view', lookPayload(looks[n]));
  };
  // Capture the last-viewed gown as the bride's "selected look" when she closes the
  // lightbox, so the form below can say "Inspired by Eva, Look 03".
  const closeLightbox = () => {
    if (lightbox != null && looks[lightbox]) setSelectedLook(looks[lightbox]);
    setLightbox(null);
    setShareFor(null);
  };
  useEffect(() => {
    if (lightbox == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight') navLook(1);
      else if (e.key === 'ArrowLeft') navLook(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightbox]); // eslint-disable-line react-hooks/exhaustive-deps
  const isLiked = (look) => !!look?.url && likes.has(look.url);
  const isSaved = (look) => !!look?.url && saves.has(look.url);
  const toggleLike = (look) => {
    if (!look?.url) return;
    setLikes((prev) => { const n = new Set(prev); const on = n.has(look.url);
      if (on) n.delete(look.url); else { n.add(look.url); upsertHouseSave(); setSelectedLook(look); }
      saveSet(lsKey('likes'), n);
      track(on ? 'boutique_look_unlike' : 'boutique_look_like', lookPayload(look)); return n; });
  };
  const toggleSave = (look) => {
    if (!look?.url) return;
    setSaves((prev) => { const n = new Set(prev); const on = n.has(look.url);
      if (on) n.delete(look.url); else { n.add(look.url); upsertHouseSave(); }
      saveSet(lsKey('saves'), n);
      track(on ? 'boutique_look_unsave' : 'boutique_look_save', lookPayload(look)); return n; });
  };
  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const doShare = (target, look) => {
    if (!look?.url) return;
    const enc = encodeURIComponent;
    const url = look.url; // public bucket image; repoint at the brand profile once it is public
    const text = `${name} · ${collectionName}${look.caption ? ' · ' + noEm(look.caption) : ''}`;
    track('boutique_look_share', { ...lookPayload(look), share_target: target });
    if (target === 'native' && canNativeShare) { navigator.share({ title: name, text, url }).catch(() => {}); setShareFor(null); return; }
    if (target === 'copy') { try { navigator.clipboard?.writeText(url); } catch {} setShareFor(null); return; }
    const hrefs = {
      pinterest: `https://www.pinterest.com/pin/create/button/?url=${enc(url)}&media=${enc(look.url)}&description=${enc(text)}`,
      facebook:  `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`,
      x:         `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(text)}`,
      whatsapp:  `https://wa.me/?text=${enc(text + ' ' + url)}`,
    };
    if (hrefs[target]) window.open(hrefs[target], '_blank', 'noopener,noreferrer,width=720,height=640');
    setShareFor(null);
  };

  // ── Current slide + its details (data-driven; every field renders only when present) ──
  const currentLook = lightbox != null ? (looks[lightbox] || null) : null;
  const lbDesigner = currentLook ? (currentLook.designer || designerC.name || '') : '';
  const lbDesc = currentLook ? (currentLook.description || currentLook.note || '') : '';
  const lbAvail = currentLook ? (currentLook.availability || galleryC.availability || '') : '';
  const lbCollection = currentLook ? (currentLook.collection || collectionName) : '';
  const lbSpecs = currentLook ? [
    ['Silhouette', currentLook.silhouette],
    ['Fabric', currentLook.fabric],
    ['Detail', currentLook.detail || currentLook.embellishment],
    ['Sizes', Array.isArray(currentLook.sizes) ? currentLook.sizes.join(', ') : currentLook.sizes],
    ['Price', currentLook.price],
  ].filter(([, v]) => v) : [];
  const lbHasDetails = Boolean(currentLook && (currentLook.caption || lbDesigner || lbDesc || lbAvail || lbSpecs.length));

  const runway = looks.slice(0, 6);
  const rest = looks.slice(6);

  const inputStyle = { width: '100%', boxSizing: 'border-box', background: WHITE,
    border: `1px solid ${LINE}`, borderRadius: 4, padding: '13px 15px',
    fontFamily: NU, fontSize: 14, color: INK, outline: 'none' };
  const Eyebrow = ({ children, light }) => (
    <p style={{ fontFamily: NU, fontWeight: 700, fontSize: 11, letterSpacing: '0.36em',
      textTransform: 'uppercase', color: light ? GOLD : INK, margin: 0 }}>{children}</p>
  );
  const Rule = () => (
    <div aria-hidden style={{ width: 'clamp(90px,14vw,200px)', height: 1, margin: '0 auto 44px',
      background: `linear-gradient(to right, transparent, ${INK}, transparent)` }} />
  );
  // Editorial "click to enlarge" cue: a slow image swell + a thin letter-spaced word on hover.
  const Cue = () => (<div className="lwd-look__cue" aria-hidden><span>View</span></div>);

  return (
    <div style={{ background: DARK, color: INK, ...palette }}>
      <style>{`
        .lwd-look__img-anim { transition: transform 1.6s cubic-bezier(.19,1,.22,1); will-change: transform; }
        .lwd-look:hover .lwd-look__img-anim { transform: scale(1.045); }
        .lwd-look__cue { position: absolute; inset: 0; z-index: 5; display: flex; align-items: center;
          justify-content: center; opacity: 0; transition: opacity .55s ease; pointer-events: none; }
        .lwd-look:hover .lwd-look__cue { opacity: 1; }
        .lwd-look__cue > span { font-family: var(--font-body); font-size: 10px; font-weight: 600;
          letter-spacing: .34em; text-transform: uppercase; color: #fbf7ef; padding: 13px 28px;
          border: 1px solid rgba(255,255,255,.6); background: rgba(16,14,12,.14);
          -webkit-backdrop-filter: blur(2px); backdrop-filter: blur(2px); }
        .lwd-look--sm .lwd-look__cue > span { font-size: 9px; letter-spacing: .24em; padding: 9px 16px; }
        @media (hover: none) {
          .lwd-look__cue { opacity: 1; align-items: flex-end; justify-content: flex-end; padding: 14px; }
          .lwd-look__cue > span { font-size: 8.5px; letter-spacing: .2em; padding: 7px 13px;
            border-color: rgba(255,255,255,.45); background: rgba(16,14,12,.42); }
          .lwd-look--sm .lwd-look__cue { padding: 8px; }
          .lwd-look--sm .lwd-look__cue > span { font-size: 7px; letter-spacing: .14em; padding: 4px 8px; }
        }
        @keyframes lwdSlideFade { from { opacity: .35; } to { opacity: 1; } }
      `}</style>
      {/* Top bar, LWD mark + view profile. Suppressed on the public showcase
          (siteChrome), where the real HomeNav masthead + menu render instead. */}
      {!siteChrome && (
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 30,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: isMobile ? '16px 18px' : '22px 34px', pointerEvents: 'none' }}>
        <span style={{ fontFamily: GD, fontSize: 22, letterSpacing: '0.18em', color: '#fbf7ef',
          textShadow: '0 1px 14px rgba(0,0,0,0.5)' }}>LWD</span>
        {onViewListing && (
          <button onClick={onViewListing} style={{ pointerEvents: 'auto', fontFamily: NU, fontSize: 10,
            fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: '#fbf7ef',
            background: 'rgba(0,0,0,0.32)', border: '1px solid rgba(255,255,255,0.35)', borderRadius: 2,
            padding: '9px 16px', cursor: 'pointer', backdropFilter: 'blur(4px)' }}>
            View Live Profile →
          </button>
        )}
      </div>
      )}

      {/* 1, The Collection: one gown, one statement. The first question is "is this me?" */}
      <section className={openImg ? 'lwd-look' : undefined} onClick={() => openImg && openLook({ url: openImg, caption: '' })} style={{ position: 'relative', height: isMobile ? '88vh' : '100vh', background: DARK, overflow: 'hidden', cursor: openImg ? 'zoom-in' : 'default' }}>
        {openImg ? (
          <img src={imgUrl(openImg, { width: 1800 })} alt="" className="lwd-look__img-anim" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 22%' }} />
        ) : null}
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.28) 0%, rgba(0,0,0,0.03) 32%, rgba(0,0,0,0.06) 58%, rgba(0,0,0,0.42) 100%)' }} />
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: isMobile ? '11%' : '13%', textAlign: 'center', padding: '0 24px' }}>
          <Eyebrow light>{eyebrowKicker}</Eyebrow>
          <h1 style={{ fontFamily: GD, fontWeight: 400, fontSize: isMobile ? 46 : 92, lineHeight: 1, color: '#fbf7ef',
            margin: '20px 0 0', letterSpacing: '-0.01em', textShadow: '0 3px 40px rgba(0,0,0,0.5)' }}>{name}</h1>
          {openStatement && (
            <p style={{ fontFamily: GD, fontStyle: 'italic', fontWeight: 400, fontSize: isMobile ? 19 : 26,
              color: 'rgba(249,248,244,0.94)', marginTop: 20, textShadow: '0 1px 20px rgba(0,0,0,0.6)' }}>{openStatement}</p>
          )}
          {onViewListing && (
            <button onClick={e => { e.stopPropagation(); onViewListing(); }} style={{ marginTop: 28, background: 'none', border: '1px solid rgba(249,248,244,0.55)', borderRadius: 0, cursor: 'pointer', padding: '10px 28px', fontFamily: NU, fontSize: 11, fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(249,248,244,0.9)', backdropFilter: 'blur(4px)' }}>
              View Full Profile
            </button>
          )}
        </div>
        {openImg && <Cue />}
      </section>

      {/* 2, The Designer */}
      {designerStory && (
        <section id="bq-house" style={{ background: CREAM, padding: isMobile ? '78px 24px' : '124px 24px', textAlign: 'center' }}>
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <Eyebrow>{designerEyebrow}</Eyebrow>
            <h2 style={{ fontFamily: GD, fontStyle: 'italic', fontWeight: 400, fontSize: isMobile ? 30 : 46,
              lineHeight: 1.18, color: INK, margin: '22px 0 0' }}>{designerHeadline}</h2>
            <p style={{ fontFamily: NU, fontWeight: 300, fontSize: isMobile ? 15 : 17, lineHeight: 1.9,
              color: INK2, margin: '28px auto 0', maxWidth: 640 }}>{designerStory}</p>
            {designerName && (
              <p style={{ fontFamily: NU, fontWeight: 600, fontSize: 12, letterSpacing: '0.22em',
                textTransform: 'uppercase', color: INK, marginTop: 30 }}>{designerName}</p>
            )}
          </div>
        </section>
      )}

      {/* 3, The Craft */}
      {showCraft && (
        <section style={{ background: WHITE, padding: isMobile ? '90px 24px' : '150px 24px', textAlign: 'center' }}>
          <div style={{ maxWidth: 820, margin: '0 auto' }}>
            <Rule />
            <Eyebrow light>{craftC.eyebrow || 'The Craft'}</Eyebrow>
            {craftHeadline && (
              <h2 style={{ fontFamily: GD, fontStyle: 'italic', fontWeight: 400, fontSize: isMobile ? 30 : 50,
                lineHeight: 1.2, color: INK, margin: '24px 0 0' }}>{craftHeadline}</h2>
            )}
            {craftBody && (
              <p style={{ fontFamily: NU, fontWeight: 300, fontSize: isMobile ? 15 : 17, lineHeight: 1.85,
                color: INK2, margin: '28px auto 0', maxWidth: 580 }}>{craftBody}</p>
            )}
            {craftQuote && (
              <p style={{ fontFamily: GD, fontStyle: 'italic', fontWeight: 400, fontSize: isMobile ? 20 : 28,
                lineHeight: 1.4, color: GOLD, margin: '40px auto 0', maxWidth: 640 }}>{craftQuote}</p>
            )}
          </div>
        </section>
      )}

      {/* 4, Featured Looks: the runway (current collection, dynamic) */}
      {runway.map((g, i) => (
        <section key={g.id || i} id={i === 0 ? 'bq-collection' : undefined} className="lwd-look" onClick={() => openLook(g)} style={{ position: 'relative', height: isMobile ? '76vh' : '100vh', background: DARK, overflow: 'hidden', cursor: 'zoom-in' }}>
          <img src={imgUrl(g.url)} alt={g.alt_text || ''} loading="lazy" className="lwd-look__img-anim"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 22%' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 58%, rgba(0,0,0,0.42) 100%)' }} />
          <div style={{ position: 'absolute', left: isMobile ? 22 : 54, bottom: isMobile ? 38 : 56 }}>
            <p style={{ fontFamily: NU, fontWeight: 700, fontSize: 10, letterSpacing: '0.3em', textTransform: 'uppercase', color: '#fbf9f5', margin: 0, textShadow: '0 1px 10px rgba(0,0,0,0.6)' }}>{collectionName}</p>
            <p style={{ fontFamily: GD, fontSize: isMobile ? 26 : 34, color: '#fbf7ef', margin: '8px 0 0', textShadow: '0 1px 16px rgba(0,0,0,0.6)' }}>{noEm(g.caption) || `Look ${String(i + 1).padStart(2, '0')}`}</p>
          </div>
          <Cue />
        </section>
      ))}

      {/* the rest of the collection: portrait grid */}
      {rest.length > 0 && (
        <section style={{ ...PALETTE_DARK, background: DARK, padding: isMobile ? '70px 18px' : '110px 48px', textAlign: 'center' }}>
          <Eyebrow>The Collection</Eyebrow>
          <h2 style={{ fontFamily: GD, fontWeight: 400, fontSize: isMobile ? 28 : 40, color: INK, margin: '18px 0 44px' }}>{collectionName}</h2>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, 1fr)',
            gap: isMobile ? 10 : 18, maxWidth: 1180, margin: '0 auto' }}>
            {rest.map((g, i) => (
              <div key={g.id || i}>
                <div onClick={() => openLook(g)} className="lwd-look lwd-look--sm" style={{ position: 'relative', aspectRatio: '2 / 3', overflow: 'hidden', background: CREAM, cursor: 'zoom-in' }}>
                  <img src={imgUrl(g.url)} alt={g.alt_text || ''} loading="lazy" className="lwd-look__img-anim"
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <Cue />
                </div>
                <p style={{ fontFamily: NU, fontSize: 11, letterSpacing: '0.04em', color: INK2, margin: '10px 0 0' }}>{noEm(g.caption) || ''}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* 5, Film (the dream beat) */}
      {filmVideo && (
        <section onMouseMove={revealFilmControls} onMouseLeave={() => setFilmControls(false)} onTouchStart={revealFilmControls}
          style={{ position: 'relative', height: isMobile ? '74vh' : '100vh', background: DARK, overflow: 'hidden' }}>
          {ytId ? (
            <iframe ref={iframeRef} src={ytSrc} title="Atelier film" allow="autoplay; encrypted-media; picture-in-picture"
              style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
                width: '177.78vh', minWidth: '100%', height: '56.25vw', minHeight: '100%', border: 0, pointerEvents: 'none' }} />
          ) : (
            <video ref={videoRef} src={filmVideo} autoPlay muted={muted} loop playsInline poster={openImg}
              onLoadedMetadata={(e) => { durationRef.current = e.currentTarget.duration || 0; }}
              onTimeUpdate={(e) => { const v = e.currentTarget; if (v.duration && !seekingRef.current) setProgress(v.currentTime / v.duration); }}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 24%' }} />
          )}
          {/* tap the film to pause on a look you love, then screenshot it */}
          <div onClick={() => { togglePlay(); revealFilmControls(); }} aria-hidden style={{ position: 'absolute', inset: 0, zIndex: 10, cursor: 'pointer' }} />

          {/* film controls, fade away during playback, return on hover/tap, stay when paused */}
          <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', left: 0, right: 0, bottom: 0, zIndex: 20,
            padding: isMobile ? '40px 16px 16px' : '64px 34px 24px', cursor: 'default',
            opacity: (!playing || filmControls) ? 1 : 0, transition: 'opacity .45s ease', pointerEvents: (!playing || filmControls) ? 'auto' : 'none',
            background: 'linear-gradient(180deg, transparent, rgba(0,0,0,0.5))' }}>
            <div onPointerDown={onScrubDown} style={{ height: 18, display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <div ref={barRef} style={{ position: 'relative', width: '100%', height: 2, background: 'rgba(255,255,255,0.3)' }}>
                <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${Math.max(0, Math.min(100, progress * 100))}%`, background: '#ffffff' }} />
                <div style={{ position: 'absolute', top: '50%', left: `${Math.max(0, Math.min(100, progress * 100))}%`, width: 9, height: 9, borderRadius: '50%', background: '#ffffff', transform: 'translate(-50%,-50%)', boxShadow: '0 0 0 3px rgba(255,255,255,0.22)' }} />
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 22, marginTop: 8 }}>
              <button type="button" onClick={togglePlay} onMouseDown={(e) => e.preventDefault()} aria-label={playing ? 'Pause' : 'Play'}
                style={{ background: 'transparent', border: 'none', color: '#fbf7ef', cursor: 'pointer', padding: 4, display: 'flex', filter: 'drop-shadow(0 1px 6px rgba(0,0,0,0.6))' }}>
                {playing ? (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="5" width="4" height="14" rx="0.5" /><rect x="14" y="5" width="4" height="14" rx="0.5" /></svg>
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>
                )}
              </button>
              <button type="button" onClick={toggleSound} onMouseDown={(e) => e.preventDefault()} aria-label={muted ? 'Unmute' : 'Mute'}
                style={{ background: 'transparent', border: 'none', color: '#fbf7ef', cursor: 'pointer', padding: 4, display: 'flex', filter: 'drop-shadow(0 1px 6px rgba(0,0,0,0.6))' }}>
                {muted ? (
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H3v6h3l5 4z" /><line x1="22" y1="9" x2="16" y2="15" /><line x1="16" y1="9" x2="22" y2="15" /></svg>
                ) : (
                  <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M11 5 6 9H3v6h3l5 4z" /><path d="M15.5 8.5a5 5 0 0 1 0 7" /><path d="M18.5 6a8 8 0 0 1 0 12" /></svg>
                )}
              </button>
            </div>
          </div>
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.22) 0%, transparent 30%, transparent 60%, rgba(0,0,0,0.4) 100%)', pointerEvents: 'none' }} />
          <div style={{ position: 'absolute', left: 0, right: 0, bottom: isMobile ? '16%' : '17%', textAlign: 'center', pointerEvents: 'none', zIndex: 14 }}>
            <Eyebrow light>{filmC.eyebrow || 'In Motion'}</Eyebrow>
            {filmC.headline && (
              <p style={{ fontFamily: GD, fontSize: isMobile ? 28 : 42, color: '#fbf7ef', margin: '12px 0 0', textShadow: '0 1px 20px rgba(0,0,0,0.6)' }}>{filmC.headline}</p>
            )}
          </div>
        </section>
      )}

      {/* 6, Experience the Collection (the centrepiece nobody else can follow) */}
      {showExperience && (
        <section id="bq-experience" style={{ position: 'relative', background: PAPER, padding: isMobile ? '100px 24px' : '170px 24px', textAlign: 'center', overflow: 'hidden' }}>
          {(experienceC.image || openImg) && (
            <img src={imgUrl(experienceC.image || openImg, { width: 1200 })} alt="" aria-hidden
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', opacity: 0.10, objectPosition: 'center 20%' }} />
          )}
          <div style={{ position: 'relative', maxWidth: 720, margin: '0 auto' }}>
            <Rule />
            <Eyebrow light>{experienceC.eyebrow || 'The Fitting Room, Reimagined'}</Eyebrow>
            <h2 style={{ fontFamily: GD, fontWeight: 400, fontSize: isMobile ? 34 : 60, lineHeight: 1.08, color: INK, margin: '24px 0 0' }}>
              {experienceC.headline || `See yourself in the ${expCollection}.`}
            </h2>
            <p style={{ fontFamily: NU, fontWeight: 300, fontSize: isMobile ? 15 : 18, lineHeight: 1.85,
              color: INK2, margin: '26px auto 0', maxWidth: 540 }}>
              {experienceC.body || 'Step inside the collection and see the gowns on you, in moments, before you ever visit the atelier.'}
            </p>
            <a href={studioHref} style={{ display: 'inline-block', marginTop: 40, fontFamily: NU, fontSize: 12, fontWeight: 700,
              letterSpacing: '0.2em', textTransform: 'uppercase', padding: '17px 40px', borderRadius: 2,
              background: INK, color: PAPER, textDecoration: 'none' }}>
              {experienceC.cta || `Experience the ${expCollection}`}
            </a>
          </div>
        </section>
      )}

      {/* 7, Real Brides */}
      {brides.length > 0 && (
        <section style={{ background: CREAM, padding: isMobile ? '78px 24px' : '124px 24px', textAlign: 'center' }}>
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <Eyebrow>{bridesC.eyebrow || 'Real Brides'}</Eyebrow>
            <h2 style={{ fontFamily: GD, fontWeight: 400, fontSize: isMobile ? 28 : 42, color: INK, margin: '18px 0 50px' }}>{bridesC.headline || 'Her words'}</h2>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : (brides.length > 1 ? '1fr 1fr' : '1fr'), gap: isMobile ? 36 : 56, maxWidth: 820, margin: '0 auto' }}>
              {brides.map((b, i) => (
                <figure key={i} style={{ margin: 0 }}>
                  <blockquote style={{ fontFamily: GD, fontStyle: 'italic', fontWeight: 400, fontSize: isMobile ? 19 : 23,
                    lineHeight: 1.5, color: INK, margin: 0 }}>{b.quote || b.body}</blockquote>
                  {(b.name || b.location) && (
                    <figcaption style={{ fontFamily: NU, fontWeight: 600, fontSize: 11, letterSpacing: '0.2em',
                      textTransform: 'uppercase', color: INK, marginTop: 22 }}>
                      {[b.name, b.location].filter(Boolean).join(' · ')}
                    </figcaption>
                  )}
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 7.2, Try On CTA: standalone Fashion Studio entry point */}
      {(tryonC.headline || tryonC.body || tryonC.cta) && (
        <section style={{ ...PALETTE_DARK, background: DARK, padding: isMobile ? '80px 24px' : '130px 24px', textAlign: 'center' }}>
          <div style={{ maxWidth: 560, margin: '0 auto' }}>
            <Eyebrow light>{tryonC.eyebrow || 'Virtual Try-On'}</Eyebrow>
            <h2 style={{ fontFamily: GD, fontStyle: 'italic', fontWeight: 400, fontSize: isMobile ? 30 : 46, color: INK, margin: '22px 0 0', lineHeight: 1.1 }}>
              {tryonC.headline || `See the gown on you.`}
            </h2>
            <p style={{ fontFamily: NU, fontWeight: 300, fontSize: isMobile ? 14 : 16, lineHeight: 1.85, color: INK2, margin: '22px auto 0', maxWidth: 460 }}>
              {tryonC.body || `Upload a photo and see any gown from the collection on you, before you step inside the atelier.`}
            </p>
            {(() => {
              const href = tryonC.href || (listing?.slug ? `/fashion-studio?boutique=${listing.slug}` : null);
              return href ? (
                <a href={href} style={{ display: 'inline-block', marginTop: 38, fontFamily: NU, fontSize: 11, fontWeight: 700,
                  letterSpacing: '0.22em', textTransform: 'uppercase', color: GOLD, borderBottom: `1px solid ${GOLD}`,
                  textDecoration: 'none', paddingBottom: 3, transition: 'opacity .2s' }}>
                  {tryonC.cta || 'Try On the Collection'}
                </a>
              ) : null;
            })()}
          </div>
        </section>
      )}

      {/* 7.4, FAQs */}
      {(faqC.items || []).length > 0 && (
        <section style={{ background: WHITE, padding: isMobile ? '80px 24px' : '130px 24px' }}>
          <div style={{ maxWidth: 760, margin: '0 auto' }}>
            <Eyebrow style={{ textAlign: 'center', display: 'block', marginBottom: 0 }}>{faqC.eyebrow || 'Questions'}</Eyebrow>
            <h2 style={{ fontFamily: GD, fontWeight: 400, fontSize: isMobile ? 28 : 42, color: INK, margin: '18px 0 54px', textAlign: 'center' }}>{faqC.headline || 'Everything you need to know.'}</h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {(faqC.items || []).map((item, i) => (
                <FaqItem key={i} q={item.q} a={item.a} INK={INK} INK2={INK2} LINE={LINE} NU={NU} GD={GD} isMobile={isMobile} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* 7.5, The Publication: the brochure after the show. The flip-magazine
          editorial, offered once the dreaming and the try-on have landed. */}
      {magazineSlug && (
        <section style={{ background: WHITE, padding: isMobile ? '78px 16px' : '120px 24px' }}>
          <div style={{ maxWidth: 1120, margin: '0 auto', textAlign: 'center' }}>
            <Eyebrow>The Publication</Eyebrow>
            <h2 style={{ fontFamily: GD, fontWeight: 400, fontSize: isMobile ? 28 : 44, color: INK, margin: '18px 0 14px' }}>
              {(listing?.name || showcase?.title || 'The atelier')}, in print.
            </h2>
            <p style={{ fontFamily: NU, fontWeight: 300, fontSize: isMobile ? 14 : 16, lineHeight: 1.8,
              color: INK2, margin: '0 auto 40px', maxWidth: 540 }}>
              The house, told as an editorial feature in LWD Magazine. Turn the pages.
            </p>
            <div style={{ overflow: 'hidden', border: `1px solid ${LINE}`, boxShadow: '0 24px 70px rgba(0,0,0,0.10)',
              padding: 14, background: CREAM }}>
              <iframe src={`/publications/embed/${magazineSlug}?preview=1`} title="Editorial feature" loading="lazy"
                style={{ width: '100%', height: isMobile ? 600 : 820, border: 0, display: 'block' }} />
            </div>
          </div>
        </section>
      )}

      {/* 8, Visit the Atelier */}
      <section style={{ background: PAPER, padding: isMobile ? '82px 22px' : '130px 24px', textAlign: 'center' }}>
        <div style={{ maxWidth: 520, margin: '0 auto' }}>
          <Eyebrow light>{atelierC.eyebrow || 'Visit the Atelier'}</Eyebrow>
          <h2 style={{ fontFamily: GD, fontStyle: 'italic', fontWeight: 400, fontSize: isMobile ? 30 : 46, color: INK, margin: '22px 0 0' }}>{atelierHeadline}</h2>
          <p style={{ fontFamily: NU, fontWeight: 300, fontSize: isMobile ? 14 : 16, lineHeight: 1.85, color: INK2, margin: '22px auto 0', maxWidth: 460 }}>
            {atelierBody}
          </p>

          {showAppointment && (apptState === 'sent' ? (
            <div style={{ marginTop: 38, fontFamily: GD, fontStyle: 'italic', fontSize: 21, color: INK }}>
              Thank you. The atelier will be in touch shortly.
            </div>
          ) : (
            <div style={{ marginTop: 34, display: 'flex', flexDirection: 'column', gap: 12, textAlign: 'left' }}>
              {(selectedLook || savedLooks.length > 0) && (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 20, padding: '18px 20px',
                  border: `1px solid ${LINE}`, marginBottom: 8 }}>
                  {selectedLook && (
                    <div style={{ flexShrink: 0, width: isMobile ? 100 : 120, aspectRatio: '2/3', overflow: 'hidden', background: CREAM }}>
                      <img src={imgUrl(selectedLook.url, { width: 1600 })} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    </div>
                  )}
                  <div style={{ flex: 1, paddingTop: 2 }}>
                    {selectedLook && (
                      <>
                        <p style={{ fontFamily: NU, fontSize: 9, fontWeight: 700, letterSpacing: '0.18em',
                          textTransform: 'uppercase', color: INK2, margin: '0 0 7px' }}>Inspired by</p>
                        <p style={{ fontFamily: GD, fontStyle: 'italic', fontWeight: 400, fontSize: isMobile ? 20 : 24,
                          color: INK, margin: 0, lineHeight: 1.15 }}>
                          {selectedLook.caption ? noEm(selectedLook.caption) : 'Your selected look'}
                        </p>
                      </>
                    )}
                    {savedLooks.length > 0 && (
                      <div style={{ marginTop: selectedLook ? 18 : 0 }}>
                        <p style={{ fontFamily: NU, fontSize: 9, fontWeight: 700, letterSpacing: '0.18em',
                          textTransform: 'uppercase', color: INK2, margin: '0 0 10px' }}>
                          {selectedLook ? 'Your saved looks' : 'Inspired by your saved looks'}
                        </p>
                        <div style={{ display: 'flex', gap: 8 }}>
                          {savedLooks.map((l, i) => (
                            <div key={l.id} style={{ width: isMobile ? 46 : 54, aspectRatio: '2/3',
                              overflow: 'hidden', background: CREAM, border: `1px solid ${LINE}`, flexShrink: 0 }}>
                              <img src={imgUrl(l.result_image_url, { width: 400 })} alt={`Look ${i + 1}`} loading="lazy"
                                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              <input value={appt.name} onChange={(e) => setAppt((a) => ({ ...a, name: e.target.value }))} placeholder="Your name" style={inputStyle} />
              <input type="email" value={appt.email} onChange={(e) => setAppt((a) => ({ ...a, email: e.target.value }))} placeholder="Your email" style={inputStyle} />
              {apptTypes.length > 0 && (
                <select value={appt.appointmentType} onChange={(e) => setAppt((a) => ({ ...a, appointmentType: e.target.value }))} style={{ ...inputStyle, color: appt.appointmentType ? INK : INK2, appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer' }}>
                  <option value="">Preferred appointment (optional)</option>
                  {apptTypes.map((t) => <option key={t} value={t} style={{ color: '#161616' }}>{t}</option>)}
                </select>
              )}
              <input value={appt.weddingLocation} onChange={(e) => setAppt((a) => ({ ...a, weddingLocation: e.target.value }))} placeholder="Wedding location (city or venue)" style={inputStyle} />
              <div>
                <label style={{ display: 'block', fontFamily: NU, fontSize: 9, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: INK2, margin: '0 0 6px 2px' }}>Wedding date (optional)</label>
                <input type="date" value={appt.weddingDate} onChange={(e) => setAppt((a) => ({ ...a, weddingDate: e.target.value }))} style={{ ...inputStyle, color: appt.weddingDate ? INK : INK2 }} />
              </div>
              <select value={appt.budget} onChange={(e) => setAppt((a) => ({ ...a, budget: e.target.value }))} style={{ ...inputStyle, color: appt.budget ? INK : INK2, appearance: 'none', WebkitAppearance: 'none', cursor: 'pointer' }}>
                <option value="">Estimated dress budget (optional)</option>
                {budgetBands.map((b) => <option key={b} value={b} style={{ color: '#161616' }}>{b}</option>)}
              </select>
              <textarea value={appt.message} onChange={(e) => setAppt((a) => ({ ...a, message: e.target.value }))} placeholder="Tell us about your wedding and the dress you imagine… (optional)" rows={3} style={{ ...inputStyle, resize: 'vertical' }} />
              {(() => {
                const ready = !!(appt.name.trim() && appt.email.trim());
                return (
                  <button onClick={submitAppt} disabled={apptState === 'sending' || !ready} style={{
                    marginTop: 6, fontFamily: NU, fontSize: 12, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase',
                    padding: '15px 0', borderRadius: 2,
                    border: ready ? '1px solid transparent' : '1px solid rgba(22,22,22,0.4)',
                    cursor: (apptState === 'sending' || !ready) ? 'default' : 'pointer',
                    background: ready ? INK : 'transparent',
                    color: ready ? PAPER : INK,
                    opacity: apptState === 'sending' ? 0.6 : 1,
                    transition: 'background .3s ease, color .3s ease, border-color .3s ease' }}>
                    {apptState === 'sending' ? 'Sending…' : 'Request an Appointment'}
                  </button>
                );
              })()}
            </div>
          ))}

          {/* Before your fitting, quiet inline ways to explore. Each link only
              shows when that beat actually exists (no empty blocks). */}
          {(runway.length > 0 || designerStory || showExperience || onViewListing) && (
            <div style={{ marginTop: 42, paddingTop: 26, borderTop: `1px solid ${LINE}` }}>
              <p style={{ fontFamily: NU, fontSize: 10, fontWeight: 700, letterSpacing: '0.22em', textTransform: 'uppercase', color: INK2, margin: '0 0 16px' }}>Before your fitting</p>
              <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: isMobile ? 18 : 30 }}>
                {[
                  runway.length > 0 && { label: 'View Collection', id: 'bq-collection' },
                  designerStory && { label: 'Explore the Atelier', id: 'bq-house' },
                  showExperience && { label: 'Try On Another Look', id: 'bq-experience' },
                ].filter(Boolean).map((l) => (
                  <button key={l.id} onClick={() => scrollTo(l.id)} style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0',
                    fontFamily: NU, fontSize: 12, fontWeight: 600, letterSpacing: '0.14em',
                    textTransform: 'uppercase', color: INK, borderBottom: `1px solid ${INK}` }}>{l.label}</button>
                ))}
                {onViewListing && (
                  <button onClick={onViewListing} style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px 0',
                    fontFamily: NU, fontSize: 12, fontWeight: 600, letterSpacing: '0.14em',
                    textTransform: 'uppercase', color: INK, borderBottom: `1px solid ${INK}` }}>View Full Profile</button>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* 9, About the Stylist / Designer */}
      {(stylistC.name || stylistC.body) && (
        <section style={{ background: WHITE, padding: isMobile ? '80px 24px' : '130px 24px' }}>
          <div style={{ maxWidth: 860, margin: '0 auto', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: 'center', gap: isMobile ? 40 : 72 }}>
            {stylistC.photo && (
              <div style={{ flexShrink: 0, width: isMobile ? 160 : 220, height: isMobile ? 200 : 280, overflow: 'hidden', filter: 'grayscale(18%)' }}>
                <img src={imgUrl(stylistC.photo)} alt={stylistC.name || ''} loading="lazy"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: stylistC.photo_position || 'center top', display: 'block' }} />
              </div>
            )}
            <div style={{ textAlign: isMobile ? 'center' : 'left' }}>
              <Eyebrow>{stylistC.eyebrow || 'Meet the Designer'}</Eyebrow>
              {stylistC.name && (
                <h2 style={{ fontFamily: GD, fontWeight: 400, fontSize: isMobile ? 26 : 38, color: INK, margin: '18px 0 0', lineHeight: 1.1 }}>{stylistC.name}</h2>
              )}
              {stylistC.title && (
                <p style={{ fontFamily: NU, fontWeight: 600, fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: INK2, margin: '10px 0 0' }}>{stylistC.title}</p>
              )}
              {stylistC.quote && (
                <blockquote style={{ fontFamily: GD, fontStyle: 'italic', fontSize: isMobile ? 18 : 22, color: INK, margin: isMobile ? '22px 0 0' : '26px 0 0', lineHeight: 1.5, borderLeft: `2px solid ${GOLD}`, paddingLeft: 20 }}>
                  {stylistC.quote}
                </blockquote>
              )}
              {stylistC.body && (
                <p style={{ fontFamily: NU, fontWeight: 300, fontSize: isMobile ? 14 : 15, lineHeight: 1.9, color: INK2, margin: '22px 0 0', maxWidth: 520 }}>
                  {stylistC.body}
                </p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Gallery slider, flip through the collection; details + like/save/share per look */}
      {currentLook && (
        <div
          onClick={closeLightbox}
          onTouchStart={(e) => { touchX.current = e.touches[0]?.clientX ?? 0; }}
          onTouchEnd={(e) => { const dx = (e.changedTouches[0]?.clientX ?? 0) - touchX.current; if (Math.abs(dx) > 45) navLook(dx < 0 ? 1 : -1); }}
          style={{ position: 'fixed', inset: 0, zIndex: 9999, background: SCRIM, ...LB_PALETTE,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: isMobile ? '16px 16px 104px' : '40px 78px 116px', cursor: 'zoom-out' }}>

          <img key={currentLook.url} src={imgUrl(currentLook.url, { width: 1800 })} alt={currentLook.caption || ''}
            onClick={(e) => { e.stopPropagation(); if (looks.length < 2) return; const r = e.currentTarget.getBoundingClientRect(); navLook((e.clientX - r.left) < r.width / 2 ? -1 : 1); }}
            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', boxShadow: '0 24px 80px rgba(0,0,0,0.6)', animation: 'lwdSlideFade .5s ease', cursor: looks.length > 1 ? 'pointer' : 'default' }} />

          {/* dress details, collection-agnostic, renders only the fields that exist */}
          {lbHasDetails && (
            <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', left: isMobile ? 16 : 40, top: isMobile ? 64 : 84,
              maxWidth: isMobile ? '74%' : 320, textAlign: 'left', cursor: 'default' }}>
              {lbCollection && <Eyebrow light>{lbCollection}</Eyebrow>}
              {currentLook.caption && (
                <p style={{ fontFamily: GD, fontSize: isMobile ? 24 : 32, lineHeight: 1.1, color: INK, margin: '10px 0 0' }}>{noEm(currentLook.caption)}</p>
              )}
              {lbDesigner && (
                <p style={{ fontFamily: NU, fontWeight: 600, fontSize: 11, letterSpacing: '0.2em', textTransform: 'uppercase', color: INK2, margin: '12px 0 0' }}>{lbDesigner}</p>
              )}
              {lbDesc && (
                <p style={{ fontFamily: NU, fontWeight: 300, fontSize: 13, lineHeight: 1.7, color: INK2, margin: '14px 0 0' }}>{lbDesc}</p>
              )}
              {lbSpecs.length > 0 && (
                <div style={{ margin: '16px 0 0', display: 'flex', flexDirection: 'column', gap: 7 }}>
                  {lbSpecs.map(([k, v]) => (
                    <div key={k} style={{ display: 'flex', gap: 12 }}>
                      <span style={{ fontFamily: NU, fontSize: 10, fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', color: INK, minWidth: 78 }}>{k}</span>
                      <span style={{ fontFamily: NU, fontSize: 13, color: INK }}>{v}</span>
                    </div>
                  ))}
                </div>
              )}
              {lbAvail && (
                <p style={{ fontFamily: NU, fontSize: 11, fontWeight: 600, letterSpacing: '0.12em', textTransform: 'uppercase', color: INK2, margin: '18px 0 0' }}>{lbAvail}</p>
              )}
            </div>
          )}

          {/* counter */}
          {looks.length > 1 && (
            <div style={{ position: 'absolute', top: isMobile ? 22 : 30, left: '50%', transform: 'translateX(-50%)',
              fontFamily: NU, fontSize: 11, fontWeight: 600, letterSpacing: '0.28em', color: INK2 }}>
              {String(lightbox + 1).padStart(2, '0')}<span style={{ opacity: 0.45 }}> / {String(looks.length).padStart(2, '0')}</span>
            </div>
          )}

          {/* prev / next */}
          {looks.length > 1 && ['prev', 'next'].map((side) => (
            <button key={side} onClick={(e) => { e.stopPropagation(); navLook(side === 'next' ? 1 : -1); }}
              aria-label={side === 'next' ? 'Next look' : 'Previous look'} style={{ position: 'absolute', top: '50%',
              [side === 'next' ? 'right' : 'left']: isMobile ? 8 : 26, transform: 'translateY(-50%)', zIndex: 6,
              width: 50, height: 50, borderRadius: '50%', border: `1px solid ${LINE}`, background: FROST,
              color: INK, cursor: 'pointer', backdropFilter: 'blur(4px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <polyline points={side === 'next' ? '9 6 15 12 9 18' : '15 6 9 12 15 18'} />
              </svg>
            </button>
          ))}

          {/* share menu (above the action bar) */}
          {shareFor && (
            <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', left: '50%', bottom: isMobile ? 86 : 104,
              transform: 'translateX(-50%)', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 8, cursor: 'default',
              background: 'rgba(22,18,14,0.96)', border: `1px solid ${LINE}`, borderRadius: 12, padding: 12, maxWidth: 380, backdropFilter: 'blur(8px)' }}>
              {[['pinterest', 'Pinterest'], ['facebook', 'Facebook'], ['x', 'X'], ['whatsapp', 'WhatsApp'],
                ...(canNativeShare ? [['native', 'Instagram / TikTok…']] : []), ['copy', 'Copy link']].map(([t, lbl]) => (
                <button key={t} onClick={() => doShare(t, currentLook)} style={{ fontFamily: NU, fontSize: 12, fontWeight: 600,
                  letterSpacing: '0.04em', padding: '10px 14px', borderRadius: 8, cursor: 'pointer',
                  border: `1px solid ${LINE}`, background: 'rgba(255,255,255,0.06)', color: INK }}>{lbl}</button>
              ))}
            </div>
          )}

          {/* action bar, one slim editorial bar, hairline-divided (no pill chrome) */}
          <div onClick={(e) => e.stopPropagation()} style={{ position: 'absolute', left: '50%', bottom: isMobile ? 26 : 42,
            transform: 'translateX(-50%)', display: 'flex', alignItems: 'stretch', cursor: 'default',
            border: `1px solid ${LINE}`, borderRadius: 3, overflow: 'hidden',
            background: FROST, backdropFilter: 'blur(6px)' }}>
            {[
              { key: 'like', label: isLiked(currentLook) ? 'Liked' : 'Like', active: isLiked(currentLook), on: () => toggleLike(currentLook),
                icon: <path d="M19 14c1.49-1.46 3-3.21 3-5.5A3.5 3.5 0 0 0 12 5 3.5 3.5 0 0 0 2 8.5c0 2.29 1.5 4.04 3 5.5l7 7Z" /> },
              { key: 'save', label: isSaved(currentLook) ? 'Saved' : 'Save', active: isSaved(currentLook), on: () => toggleSave(currentLook),
                icon: <path d="M6 3h12a1 1 0 0 1 1 1v17l-7-4-7 4V4a1 1 0 0 1 1-1Z" /> },
              { key: 'share', label: 'Share', active: !!shareFor, on: () => setShareFor(shareFor ? null : currentLook),
                icon: <g><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.6" y1="13.5" x2="15.4" y2="17.5" /><line x1="15.4" y1="6.5" x2="8.6" y2="10.5" /></g> },
            ].map((b, i) => (
              <button key={b.key} onClick={b.on} aria-label={b.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 9,
                fontFamily: NU, fontSize: 10.5, fontWeight: 600, letterSpacing: '0.24em', textTransform: 'uppercase',
                padding: isMobile ? '13px 18px' : '14px 26px', cursor: 'pointer', border: 'none',
                borderLeft: i > 0 ? `1px solid ${LINE}` : 'none',
                background: b.active ? 'rgba(255,255,255,0.09)' : 'transparent',
                color: b.active ? INK : INK2, transition: 'background .25s ease, color .25s ease' }}>
                <svg width="15" height="15" viewBox="0 0 24 24" fill={b.active && b.key !== 'share' ? GOLD : 'none'}
                  stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">{b.icon}</svg>
                {b.label}
              </button>
            ))}
          </div>

          <button onClick={(e) => { e.stopPropagation(); closeLightbox(); }} aria-label="Close" style={{ position: 'absolute',
            top: 20, right: 24, width: 42, height: 42, borderRadius: '50%', border: `1px solid ${LINE}`,
            background: FROST, color: INK, fontSize: 18, cursor: 'pointer', zIndex: 7 }}>✕</button>
        </div>
      )}
    </div>
  );
}
